import React, { useState, useEffect, useCallback } from "react";
import { toast } from "sonner";
import PageHeader from "@/components/shared/PageHeader";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import {
  Wifi, WifiOff, RefreshCcw, Trash2, AlertTriangle, CheckCircle2,
  Clock, CloudUpload, Loader2,
} from "lucide-react";
import moment from "moment";
import { triggerSync } from "@/swRegistration";

export default function SyncStatus() {
  const [online, setOnline] = useState(navigator.onLine);
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [syncing, setSyncing] = useState(false);
  const [showClearDialog, setShowClearDialog] = useState(false);

  const refreshQueue = useCallback(async () => {
    setLoading(true);
    if (!navigator.serviceWorker?.controller) {
      setItems([]);
      setLoading(false);
      return;
    }
    navigator.serviceWorker.controller.postMessage({ type: "GET_OFFLINE_QUEUE" });
  }, []);

  useEffect(() => {
    refreshQueue();
    const onMsg = (e) => {
      if (e.data?.type === "OFFLINE_QUEUE_LIST") {
        setItems(e.data.items || []);
        setLoading(false);
      }
      if (e.data?.type === "OFFLINE_SYNC_COMPLETE") {
        setSyncing(false);
        if (e.data.synced > 0) toast.success(e.data.message);
        else if (e.data.failed > 0) toast.warning(e.data.message);
        refreshQueue();
      }
      if (e.data?.type === "OFFLINE_QUEUE_UPDATED") {
        refreshQueue();
      }
    };
    if (navigator.serviceWorker) {
      navigator.serviceWorker.addEventListener("message", onMsg);
    }
    const onOnline = () => setOnline(true);
    const onOffline = () => setOnline(false);
    window.addEventListener("online", onOnline);
    window.addEventListener("offline", onOffline);
    return () => {
      navigator.serviceWorker?.removeEventListener("message", onMsg);
      window.removeEventListener("online", onOnline);
      window.removeEventListener("offline", onOffline);
    };
  }, [refreshQueue]);

  const handleSync = () => {
    if (!online) {
      toast.error("No hi ha connexió. Connecta't per sincronitzar.");
      return;
    }
    setSyncing(true);
    triggerSync();
    setTimeout(() => setSyncing(false), 30000);
  };

  const handleClear = (id) => {
    if (!navigator.serviceWorker?.controller) return;
    navigator.serviceWorker.controller.postMessage({ type: "CLEAR_OFFLINE_QUEUE_ITEM", id });
    setTimeout(refreshQueue, 300);
    toast.success("Operació eliminada de la cua");
  };

  const handleClearAll = () => {
    if (!navigator.serviceWorker?.controller) return;
    navigator.serviceWorker.controller.postMessage({ type: "CLEAR_ALL_OFFLINE_QUEUE" });
    setTimeout(refreshQueue, 300);
    setShowClearDialog(false);
    toast.success("Tota la cua eliminada");
  };

  const failedItems = items.filter((i) => i.retries >= 5);
  const pendingItems = items.filter((i) => i.retries < 5);

  return (
    <div className="space-y-5" data-testid="sync-status-page">
      <PageHeader
        title="Estat de Sincronització"
        subtitle="Operacions guardades offline pendents de sincronitzar"
        actions={
          <Button
            onClick={handleSync}
            disabled={syncing || !online || items.length === 0}
            className="bg-[#7B2D3F] hover:bg-[#5B1D2F] gap-2 rounded-xl"
            data-testid="sync-now-btn"
          >
            {syncing ? <Loader2 className="w-4 h-4 animate-spin" /> : <RefreshCcw className="w-4 h-4" />}
            Sincronitzar ara
          </Button>
        }
      />

      {/* Connection status banner */}
      <Card className={`p-4 border-0 shadow-sm ${online ? "bg-emerald-50" : "bg-amber-50"}`}>
        <div className="flex items-center gap-3">
          {online ? (
            <Wifi className="w-6 h-6 text-emerald-600" />
          ) : (
            <WifiOff className="w-6 h-6 text-amber-600" />
          )}
          <div className="flex-1">
            <p className={`font-semibold ${online ? "text-emerald-900" : "text-amber-900"}`}>
              {online ? "Connectat a internet" : "Sense connexió"}
            </p>
            <p className={`text-sm ${online ? "text-emerald-700" : "text-amber-700"}`}>
              {online
                ? items.length === 0
                  ? "Totes les dades estan sincronitzades."
                  : `${items.length} operació(ns) pendents de sincronitzar.`
                : "Les operacions que facis ara es sincronitzaran automàticament quan torni la connexió."}
            </p>
          </div>
        </div>
      </Card>

      {/* KPIs */}
      <div className="grid grid-cols-3 gap-3">
        <Card className="p-4 border-0 shadow-sm">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-blue-50 flex items-center justify-center">
              <Clock className="w-5 h-5 text-blue-600" />
            </div>
            <div>
              <p className="text-xs text-gray-500">Pendents</p>
              <p className="text-xl font-semibold text-gray-900">{pendingItems.length}</p>
            </div>
          </div>
        </Card>
        <Card className="p-4 border-0 shadow-sm">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-red-50 flex items-center justify-center">
              <AlertTriangle className="w-5 h-5 text-red-600" />
            </div>
            <div>
              <p className="text-xs text-gray-500">Amb errors</p>
              <p className="text-xl font-semibold text-gray-900">{failedItems.length}</p>
            </div>
          </div>
        </Card>
        <Card className="p-4 border-0 shadow-sm">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-emerald-50 flex items-center justify-center">
              <CheckCircle2 className="w-5 h-5 text-emerald-600" />
            </div>
            <div>
              <p className="text-xs text-gray-500">Estat</p>
              <p className="text-xl font-semibold text-gray-900">{items.length === 0 ? "OK" : "..."}</p>
            </div>
          </div>
        </Card>
      </div>

      {loading ? (
        <Card className="p-8 text-center border-dashed">
          <Loader2 className="w-6 h-6 animate-spin mx-auto text-gray-400" />
        </Card>
      ) : items.length === 0 ? (
        <Card className="p-8 text-center border-dashed">
          <CloudUpload className="w-10 h-10 mx-auto text-gray-400 mb-3" />
          <p className="text-gray-600 font-medium">Cap operació pendent</p>
          <p className="text-xs text-gray-500 mt-1">Totes les dades estan sincronitzades amb el servidor</p>
        </Card>
      ) : (
        <div className="space-y-2">
          <div className="flex justify-between items-center">
            <p className="text-sm text-gray-600">{items.length} operació(ns)</p>
            <Button
              size="sm"
              variant="outline"
              onClick={() => setShowClearDialog(true)}
              className="gap-1.5 text-red-600 hover:text-red-700"
              data-testid="clear-all-queue-btn"
            >
              <Trash2 className="w-3.5 h-3.5" /> Eliminar-ho tot
            </Button>
          </div>
          {items.map((item) => {
            const failed = (item.retries || 0) >= 5;
            const url = item.url.replace(/^https?:\/\/[^/]+/, "");
            return (
              <Card
                key={item.id}
                className={`p-3 border-0 shadow-sm ${failed ? "bg-red-50" : "bg-white"}`}
                data-testid={`queue-item-${item.id}`}
              >
                <div className="flex items-start justify-between gap-3">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <Badge variant="outline" className="text-xs font-mono">{item.method}</Badge>
                      <span className="text-sm font-medium text-gray-900 truncate" title={url}>
                        {url.length > 60 ? url.slice(0, 60) + "…" : url}
                      </span>
                      {failed && (
                        <Badge className="text-xs bg-red-100 text-red-700">Error · {item.retries} intents</Badge>
                      )}
                      {!failed && item.retries > 0 && (
                        <Badge variant="outline" className="text-xs">Reintents: {item.retries}</Badge>
                      )}
                    </div>
                    <p className="text-xs text-gray-500 mt-1">
                      {moment(item.timestamp).fromNow()}
                      {item.last_error && <span className="text-red-600 ml-2">· {item.last_error}</span>}
                    </p>
                  </div>
                  <Button
                    size="icon"
                    variant="ghost"
                    onClick={() => handleClear(item.id)}
                    title="Eliminar"
                    data-testid={`delete-queue-${item.id}`}
                  >
                    <Trash2 className="w-4 h-4 text-red-500" />
                  </Button>
                </div>
              </Card>
            );
          })}
        </div>
      )}

      <AlertDialog open={showClearDialog} onOpenChange={setShowClearDialog}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Eliminar tota la cua?</AlertDialogTitle>
            <AlertDialogDescription>
              Es perdran totes les operacions pendents que no s'han pogut sincronitzar.
              Aquesta acció no es pot desfer.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel·lar</AlertDialogCancel>
            <AlertDialogAction onClick={handleClearAll} className="bg-red-600 hover:bg-red-700">
              Eliminar-ho tot
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
